import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Calculator, Copy, Share2, ChevronDown, ChevronUp, IndianRupee, Percent, Calendar, PiggyBank } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

export default function LoanCalculatorPage() {
  const [propertyPrice, setPropertyPrice] = useState(2500000);
  const [downPayment, setDownPayment] = useState(500000);
  const [interestRate, setInterestRate] = useState(8.5);
  const [tenure, setTenure] = useState(20);
  const [showFormulas, setShowFormulas] = useState({});

  const loanAmount = propertyPrice - downPayment;

  const calculations = useMemo(() => {
    const principal = loanAmount;
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = tenure * 12;

    // EMI Formula: P * r * (1+r)^n / ((1+r)^n - 1)
    const emi = principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments) / (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    const totalAmount = emi * numberOfPayments;
    const totalInterest = totalAmount - principal;

    return {
      emi: isNaN(emi) ? 0 : emi,
      totalAmount: isNaN(totalAmount) ? 0 : totalAmount,
      totalInterest: isNaN(totalInterest) ? 0 : totalInterest,
      principal
    };
  }, [loanAmount, interestRate, tenure]);

  const chartData = [
    { name: 'Principal', value: calculations.principal, color: '#ef7a27' },
    { name: 'Interest', value: calculations.totalInterest, color: '#791b02' }
  ];

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} Lakhs`;
    return `₹${Math.round(amount).toLocaleString('en-IN')}`;
  };

  const copyResults = () => {
    const text = `
Loan Calculator Results
-----------------------
Property Price: ${formatCurrency(propertyPrice)}
Down Payment: ${formatCurrency(downPayment)}
Loan Amount: ${formatCurrency(loanAmount)}
Interest Rate: ${interestRate}%
Tenure: ${tenure} years

Monthly EMI: ${formatCurrency(calculations.emi)}
Total Amount Payable: ${formatCurrency(calculations.totalAmount)}
Total Interest: ${formatCurrency(calculations.totalInterest)}
    `.trim();
    navigator.clipboard.writeText(text);
    toast.success('Results copied to clipboard');
  };

  const shareResults = () => {
    const params = new URLSearchParams({
      price: propertyPrice,
      down: downPayment,
      rate: interestRate,
      tenure: tenure
    });
    const url = `${window.location.origin}/loan-calculator?${params.toString()}`;
    navigator.clipboard.writeText(url);
    toast.success('Shareable link copied');
  };

  const toggleFormula = (key) => {
    setShowFormulas(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const results = [
    {
      key: 'emi',
      label: 'Monthly EMI',
      value: calculations.emi,
      icon: Calendar,
      formula: 'EMI = P × r × (1+r)^n / ((1+r)^n - 1)\nWhere P = Principal, r = Monthly interest rate, n = Number of months'
    },
    {
      key: 'total',
      label: 'Total Amount Payable',
      value: calculations.totalAmount,
      icon: IndianRupee,
      formula: 'Total = EMI × Number of months'
    },
    {
      key: 'interest',
      label: 'Total Interest',
      value: calculations.totalInterest,
      icon: Percent,
      formula: 'Interest = Total Amount - Principal Amount'
    }
  ];

  return (
    <div className="pt-16 lg:pt-20" data-testid="loan-calculator-page">
      {/* Header */}
      <section className="bg-gradient-to-r from-brand-rust to-brand-orange py-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center text-white">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Calculator className="h-8 w-8" />
            <h1 className="font-heading text-4xl lg:text-5xl font-bold">
              Loan Calculator
            </h1>
          </div>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">
            Calculate your home loan EMI, total interest, and create a payment plan
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <PiggyBank className="h-5 w-5 text-brand-orange" />
                  <span>Loan Details</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Property Price */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Total Property Price</Label>
                    <span className="font-semibold text-brand-rust">{formatCurrency(propertyPrice)}</span>
                  </div>
                  <Slider
                    value={[propertyPrice]}
                    onValueChange={([v]) => setPropertyPrice(v)}
                    min={500000}
                    max={50000000}
                    step={100000}
                    className="w-full"
                    data-testid="price-slider"
                  />
                  <Input
                    type="number"
                    value={propertyPrice}
                    onChange={(e) => setPropertyPrice(Number(e.target.value))}
                    className="text-right"
                    data-testid="price-input"
                  />
                </div>

                {/* Down Payment */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Down Payment</Label>
                    <span className="font-semibold text-brand-rust">{formatCurrency(downPayment)}</span>
                  </div>
                  <Slider
                    value={[downPayment]}
                    onValueChange={([v]) => setDownPayment(v)}
                    min={0}
                    max={propertyPrice * 0.5}
                    step={50000}
                    className="w-full"
                    data-testid="downpayment-slider"
                  />
                  <Input
                    type="number"
                    value={downPayment}
                    onChange={(e) => setDownPayment(Number(e.target.value))}
                    className="text-right"
                    data-testid="downpayment-input"
                  />
                </div>

                {/* Loan Amount (auto-calculated) */}
                <div className="p-4 bg-gradient-to-r from-orange-50 to-transparent rounded-xl">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Loan Amount</span>
                    <span className="font-heading text-2xl font-bold text-gradient">
                      {formatCurrency(loanAmount)}
                    </span>
                  </div>
                </div>

                {/* Interest Rate */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Interest Rate (% per annum)</Label>
                    <span className="font-semibold text-brand-rust">{interestRate}%</span>
                  </div>
                  <Slider
                    value={[interestRate]}
                    onValueChange={([v]) => setInterestRate(v)}
                    min={5}
                    max={15}
                    step={0.1}
                    className="w-full"
                    data-testid="rate-slider"
                  />
                  <Input
                    type="number"
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    step="0.1"
                    className="text-right"
                    data-testid="rate-input"
                  />
                </div>

                {/* Tenure */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Loan Tenure (years)</Label>
                    <span className="font-semibold text-brand-rust">{tenure} years</span>
                  </div>
                  <Slider
                    value={[tenure]}
                    onValueChange={([v]) => setTenure(v)}
                    min={1}
                    max={30}
                    step={1}
                    className="w-full"
                    data-testid="tenure-slider"
                  />
                  <Input
                    type="number"
                    value={tenure}
                    onChange={(e) => setTenure(Number(e.target.value))}
                    className="text-right"
                    data-testid="tenure-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Results Section */}
            <div className="space-y-6">
              {/* Chart */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle>Payment Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Result Cards */}
              <div className="space-y-4">
                {results.map((result) => (
                  <Card key={result.key} className="calc-result-card shadow-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-brand-orange to-brand-rust rounded-lg flex items-center justify-center">
                            <result.icon className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">{result.label}</p>
                            <p className="font-heading text-2xl font-bold text-gray-900">
                              {formatCurrency(result.value)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleFormula(result.key)}
                        className="flex items-center text-xs text-gray-500 hover:text-brand-orange mt-3"
                      >
                        {showFormulas[result.key] ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
                        Show formula
                      </button>
                      {showFormulas[result.key] && (
                        <pre className="text-xs text-gray-600 mt-2 bg-gray-50 p-3 rounded whitespace-pre-wrap font-mono">
                          {result.formula}
                        </pre>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Actions */}
              <div className="flex space-x-3">
                <Button variant="outline" onClick={copyResults} className="flex-1" data-testid="copy-btn">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Results
                </Button>
                <Button variant="outline" onClick={shareResults} className="flex-1" data-testid="share-btn">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-gray-50 border-t">
        <div className="container-custom text-center">
          <p className="text-sm text-gray-500">
            <strong>Disclaimer:</strong> These figures are indicative and for informational purposes only. 
            Actual loan terms, interest rates, and EMI may vary based on the lender's policies, 
            your credit score, and other factors. Please consult with your bank or financial institution for accurate quotes.
          </p>
        </div>
      </section>
    </div>
  );
}
